import type {
    EventFeatureKey,
    EventFeaturePreset,
    EventFeaturesDTO,
} from "../../domain/contracts/event-features";
import {
    EVENT_FEATURE_KEYS,
    detectEventFeaturePreset,
    eventFeaturesFromPreset,
    eventHasFeature,
} from "../../domain/services/event-features";

export {
    EVENT_FEATURE_KEYS, detectEventFeaturePreset,
    eventFeaturesFromPreset,
    eventHasFeature
};

export const EVENT_PRESET_LABELS: Record<EventFeaturePreset, string> = {
  performance_show: "ງານສະແດง (preset)",
  manual_event: "ງານ Event (preset)",
  organizer_kol_media: "Organizer (KOL/Media)",
  custom: "ກຳນົດເອງ",
};

export const EVENT_PRESET_DESCRIPTIONS: Record<EventFeaturePreset, string> = {
  performance_show: "ນັກສະແດງ + ຊ້ອມ + ສະແດງ + ເອກະສານ + ຄ່ານາຍໜ້າ + Prop",
  manual_event: "Staff + ເອກະສານ + ຄ່ານາຍໜ້າ + Prop",
  organizer_kol_media:
    "Outsource + ເອກະສານ (AR) + ຄ່ານາຍໜ້າ + Prop — ຈັດຫາ KOL/Media",
  custom: "ເລືອກ feature ແຕ່ລະອັນເອງ",
};

export const EVENT_FEATURE_LABELS: Record<EventFeatureKey, string> = {
  performers: "ນັກສະແດງ",
  rehearsals: "ວັນຊ້ອມ",
  event_shows: "ຫຼາຍມື້ສະແດງ",
  documents: "ເອກະສານ (QT/INV)",
  commissions: "ຄ່ານາຍໜ້າ",
  staff: "Staff",
  production_costs: "Prop / ວັດສະດຸ",
  outsources: "Outsource (KOL/Media)",
};

export const EVENT_FEATURE_DESCRIPTIONS: Record<EventFeatureKey, string> = {
  performers: "ຈັດທີມນັກສະແດງ ແລະ ໃບເບີກຈ່າຍ performer",
  rehearsals: "ຕ້ອງເປີດນັກສະແດງກ່ອນ",
  event_shows: "ຕາຕະລາງຫຼາຍມື້ສະແດງ",
  documents: "ໃບສະເໜີລາຄາ / ໃບເກັບເງິນ",
  commissions: "ຄ່ານາຍໜ້າ / broker",
  staff: "ຈັດ Staff roster ແລະ ໃບເບີກຈ່າຍ staff",
  production_costs: "ບັນທຶກຕົ້ນທຸນ prop / ຊຸດ / ວັດສະດຸຕໍ່ງານ",
  outsources: "AP ຈ້າງ KOL / media / vendor (ຕົ້ນທຸນຈິງ) — ຄືກັນກັບ AR ໃນເອກະສານ",
};

/** Feature keys shown as toggles in the event form (order matters). */
export const EVENT_FORM_FEATURE_KEYS: EventFeatureKey[] = [
  "performers",
  "rehearsals",
  "event_shows",
  "staff",
  "documents",
  "commissions",
  "production_costs",
  "outsources",
];

export function activeEventFeatureKeys(
  features: EventFeaturesDTO,
): EventFeatureKey[] {
  return EVENT_FEATURE_KEYS.filter((key) => eventHasFeature(features, key));
}

export function eventFeatureSummaryLabels(
  features: EventFeaturesDTO,
): string[] {
  const preset = detectEventFeaturePreset(features);
  if (preset !== "custom") {
    return [EVENT_PRESET_LABELS[preset]];
  }
  return activeEventFeatureKeys(features).map((key) => EVENT_FEATURE_LABELS[key]);
}

export function defaultFeaturesForForm(
  preset: EventFeaturePreset = "performance_show",
): EventFeaturesDTO {
  return eventFeaturesFromPreset(preset);
}

export function mergeLockedEventFeatures(
  features: EventFeaturesDTO,
  initial: EventFeaturesDTO | undefined,
  lockedFeatures: EventFeatureKey[],
): EventFeaturesDTO {
  if (!initial || lockedFeatures.length === 0) return features;
  const merged = { ...features };
  for (const key of lockedFeatures) {
    merged[key] = initial[key];
  }
  return merged;
}

export function isEventFeatureLocked(
  key: EventFeatureKey,
  lockedFeatures: EventFeatureKey[],
): boolean {
  return lockedFeatures.includes(key);
}
